import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Books, Trophy, ChartLineUp, Play, Certificate, Compass } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function LearnerDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, enrollmentsRes] = await Promise.all([
        api.get('/reports/dashboard'),
        api.get('/enrollments/my')
      ]);
      setStats(statsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="learner-dashboard"
        title="Dashboard"
        subtitle={`Welcome back, ${user?.first_name || ''}!`}
        onBack={null}
        findable={false}
        onRefresh={fetchData}
        refreshing={loading}
        loading={loading}
        maxWidth="none"
        primary={[
          { key: 'catalog', label: 'Browse catalog', icon: Compass, onClick: () => navigate('/learn/catalog') },
        ]}
        secondary={[
          { key: 'certificates', label: 'Certificates', icon: Certificate, onClick: () => navigate('/learn/certificates') },
        ]}
      >
        {/* KPIs */}
        <StatTiles items={[
          { label: 'Enrolled', value: stats.my_enrollments || 0, icon: Books },
          { label: 'In progress', value: stats.in_progress_courses || 0, icon: ChartLineUp, iconClass: 'bg-amber-100 text-amber-700' },
          { label: 'Completed', value: stats.completed_courses || 0, icon: Trophy, iconClass: 'bg-emerald-100 text-emerald-700' },
        ]} />

        {/* My learning */}
        <DataGrid
          title="My learning"
          rows={enrollments}
          loading={loading}
          onRefresh={fetchData}
          selectable={false}
          pagination={false}
          dense
          searchKeys={['course_title']}
          columns={[
            { key: 'course_title', header: 'Course', accessor: (e) => e.course_title || '', className: 'font-medium' },
            {
              key: 'status', header: 'Status', width: 130, accessor: (e) => e.status || '',
              render: (e) => (
                e.status === 'completed' ? <StatusBadge value="completed" />
                  : e.status === 'in_progress' ? <StatusBadge value="in_progress" />
                    : e.status === 'enrolled' ? <StatusBadge value="not_started" />
                      : <StatusBadge value={e.status} />
              ),
            },
            {
              key: 'progress_percentage', header: 'Progress', width: 170, accessor: (e) => e.progress_percentage || 0,
              render: (e) => (
                <div className="flex items-center gap-2">
                  <Progress value={e.progress_percentage || 0} className="w-24 h-1.5" />
                  <span className="text-xs font-medium tabular-nums">{Math.round(e.progress_percentage || 0)}%</span>
                </div>
              ),
            },
            { key: 'enrolled_at', header: 'Enrolled', width: 120, render: (e) => (e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() : '—'), className: 'text-muted-foreground' },
          ]}
          rowTone={(e) => (e.status === 'completed' ? 'success' : null)}
          onRowDoubleClick={(e) => navigate(`/learn/course/${e.course_id}`)}
          rowActions={(e) => (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={(ev) => { ev.stopPropagation(); navigate(`/learn/course/${e.course_id}`); }}
              data-testid={`continue-course-${e.course_id}`}
            >
              <Play className="w-3.5 h-3.5" weight="fill" />
              Continue
            </Button>
          )}
          emptyMessage="You haven't enrolled in any courses yet. Browse the catalog and find courses that interest you."
        />
      </RecordPage>
    </DashboardLayout>
  );
}
